import { toast } from "react-toastify";

/**
 * Validate that invoice module and line items exist
 */
export const validateInvoiceModule = (capexInvoiceModule) => {
  if (!capexInvoiceModule?.invoice_module) {
    toast.error("Invoice Details are missing!");
    return false;
  }
  if (!capexInvoiceModule?.line_items) {
    toast.error("Invoice items are missing!");
    return false;
  }
  return true;
};

/**
 * Collect selected line item labels and total amount
 */
export const collectLineItems = (lineItems) => {
  const allSelectedLabels = [];
  let totalAmount = 0;

  for (const invoiceIndex in lineItems) {
    const invoice = lineItems[invoiceIndex];
    for (const rowIndex in invoice) {
      const row = invoice[rowIndex];
      const selected = row?.selected || [];
      selected.forEach((id) => allSelectedLabels.push(id));
      totalAmount += parseFloat(row?.amount || 0);
    }
  }

  return { allSelectedLabels, totalAmount };
};

/**
 * Convert a percentage with at most two decimal places to hundredths.
 * Example: "80.53" => 8053.
 */
export const percentageToHundredths = (value) => {
  const raw = String(value ?? "").trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) return null;

  const [whole, fraction = ""] = raw.split(".");
  const hundredths =
    Number.parseInt(whole, 10) * 100 +
    Number.parseInt(fraction.padEnd(2, "0") || "0", 10);

  if (!Number.isSafeInteger(hundredths)) return null;
  return hundredths;
};

/**
 * Parse a milestone/retention percentage. Allows high-precision legacy values
 * (e.g. 80.53302433) that were reverse-derived from whole-rupee commercial splits.
 */
export const parseMilestonePercentage = (value) => {
  const raw = String(value ?? "").trim();
  if (!/^\d+(?:\.\d{1,10})?$/.test(raw)) return null;

  const percentage = Number(raw);
  if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
    return null;
  }
  return percentage;
};

export const percentagesTotalIsHundred = (percentages, tolerance = 1e-6) => {
  if (!Array.isArray(percentages) || percentages.length === 0) return false;
  const total = percentages.reduce((sum, value) => sum + value, 0);
  return Math.abs(total - 100) <= tolerance;
};

export const normalizePercentagesToTwoDecimals = (values) => {
  const percentages = values.map(Number);
  if (
    percentages.length === 0 ||
    percentages.some(
      (percentage) =>
        !Number.isFinite(percentage) || percentage <= 0 || percentage > 100
    )
  ) {
    return values;
  }

  const rawHundredths = percentages.map((percentage) => percentage * 100);
  const rawTotal = rawHundredths.reduce((sum, value) => sum + value, 0);
  if (Math.abs(rawTotal - 10000) > 0.000001) {
    return percentages.map((percentage) => percentage.toFixed(2));
  }

  const normalized = rawHundredths.map((value, index) => ({
    index,
    hundredths: Math.floor(value),
    remainder: value - Math.floor(value),
  }));
  const allocated = normalized.reduce(
    (sum, item) => sum + item.hundredths,
    0
  );
  const remaining = 10000 - allocated;
  const remainderOrder = [...normalized].sort(
    (a, b) => b.remainder - a.remainder || a.index - b.index
  );
  for (let i = 0; i < remaining; i += 1) {
    remainderOrder[i % remainderOrder.length].hundredths += 1;
  }

  return normalized.map((item) => (item.hundredths / 100).toFixed(2));
};

/**
 * Allocate whole-rupee installment amounts from percentage values (0–100 scale)
 * using the largest-remainder method so the parts always sum to `totalAmount`.
 *
 * Keeps full percentage precision — critical when % was reverse-derived from
 * commercial amounts (e.g. 695000/863000 → 80.53302433%).
 */
export const allocateWholeRupeeAmounts = (totalAmount, percentageValues) => {
  const total = Number(totalAmount);
  if (
    !Number.isSafeInteger(total) ||
    total <= 0 ||
    total > 9999999999
  ) {
    return null;
  }

  const percentages = (percentageValues || []).map(Number);
  if (
    percentages.length === 0 ||
    percentages.some(
      (percentage) =>
        !Number.isFinite(percentage) || percentage <= 0 || percentage > 100
    )
  ) {
    return null;
  }

  const allocations = percentages.map((percentage, index) => {
    const exact = (total * percentage) / 100;
    const amount = Math.floor(exact);
    return {
      index,
      amount,
      remainder: exact - amount,
    };
  });
  let remaining =
    total - allocations.reduce((sum, item) => sum + item.amount, 0);
  if (remaining < 0) {
    return null;
  }
  const remainderOrder = [...allocations].sort(
    (a, b) => b.remainder - a.remainder || a.index - b.index
  );
  for (let i = 0; i < remaining; i += 1) {
    remainderOrder[i % remainderOrder.length].amount += 1;
  }
  return allocations.map((item) => item.amount);
};

/**
 * Validate retention data (amount + required fields)
 */
export const validateRetention = (retention = []) => {
  for (const r of retention) {
    if (
      r.amount !== "" &&
      (r.description === "" ||
        r.months === "" ||
        r.post_stage === "" ||
        r.post_stage_value === "")
    ) {
      toast.error("Retention data is missing.");
      return false;
    }
  }
  return true;
};

/**
 * Validate milestones:
 * - At least one milestone selected
 * - Advance required if convert_pi_ti = yes
 * - Amount and description required per milestone
 */
export const validateMilestones = (invoiceModule, newLineItems) => {
  const hasSelected = newLineItems.some((line) => line?.selected?.length);
  if (!hasSelected) {
    toast.error("At least one milestone should be selected.");
    return false;
  }

  if (invoiceModule.convert_pi_ti === "yes") {
    const hasAdvance = newLineItems.some((line) =>
      line?.selected?.includes(3051)
    );
    if (!hasAdvance) {
      toast.error("Advance should be selected if convert PI to TI is yes.");
      return false;
    }
  }

  for (const line of newLineItems) {
    if (line?.selected?.length) {
      if (!line.amount || isNaN(line.amount)) {
        toast.error("Amount is missing for a selected milestone.");
        return false;
      }
      if (!line.description || line.description.trim() === "") {
        toast.error("Description is missing for a selected milestone.");
        return false;
      }
    }
  }

  return true;
};

/**
 * Validate frequency = 1 (single invoice mode)
 */
export const validateFrequencyOne = (invoiceModule, allSelectedLabels) => {
  const missing = [];

  if (invoiceModule.hardware_amount > 0 && !allSelectedLabels.includes(3030)) {
    missing.push("Hardware");
  }

  if (invoiceModule.invoice_consumable_boq_id === 2) {
    if (
      invoiceModule.installation_amount > 0 &&
      !allSelectedLabels.includes(3046)
    ) {
      missing.push("Labour");
    }
  } else {
    if (
      invoiceModule.installation_amount > 0 &&
      !allSelectedLabels.includes(3031)
    ) {
      missing.push("Installation");
    }
  }

  if (invoiceModule.cartage_amount > 0 && !allSelectedLabels.includes(3032)) {
    missing.push("Cartage");
  }

  if (invoiceModule.tag_amount > 0 && !allSelectedLabels.includes(3033)) {
    missing.push("Tag");
  }

  if (missing.length > 0) {
    toast.error(
      `${missing.join(", ")} are missing in invoice line based on their amount.`
    );
    return false;
  }

  return true;
};

/**
 * Main invoice validator (entry point)
 */
export const invoiceValidator = (capexInvoiceModule) => {
  if (!validateInvoiceModule(capexInvoiceModule)) return false;

  const rawLineItems = capexInvoiceModule.line_items;
  const newLineItems = Object.values(rawLineItems).flatMap((group) =>
    Object.values(group)
  );

  // Frequency 2 (milestones)
  if (capexInvoiceModule.invoice_module.invoice_frequency_id === 2) {
    if (!validateMilestones(capexInvoiceModule.invoice_module, newLineItems))
      return false;
    if (!validateRetention(capexInvoiceModule.retention)) return false;

    const percentageTargets = [];
    for (const line of newLineItems) {
      if (!line?.selected?.length) continue;
      percentageTargets.push({ item: line, label: "Milestone" });
    }
    for (const retention of capexInvoiceModule.retention || []) {
      if (
        retention?.amount === "" ||
        retention?.amount === null ||
        retention?.amount === undefined
      ) {
        continue;
      }
      percentageTargets.push({ item: retention, label: "Retention" });
    }

    const percentages = [];
    for (const { item, label } of percentageTargets) {
      const percentage = parseMilestonePercentage(item.amount);
      if (percentage === null) {
        toast.error(
          `${label} percentage must be greater than 0, at most 100, and have no more than 10 decimal places.`
        );
        return false;
      }
      percentages.push(percentage);
    }

    if (!percentagesTotalIsHundred(percentages)) {
      const percentageTotal = percentages.reduce((sum, value) => sum + value, 0);
      toast.error(
        `Total milestone and retention percentage should be 100.00% but it is ${percentageTotal.toFixed(8)}%.`
      );
      return false;
    }

    const allocatedAmounts = allocateWholeRupeeAmounts(
      capexInvoiceModule.invoice_module.total_amount,
      percentages
    );
    if (!allocatedAmounts) {
      toast.error("Total amount must be a positive whole-rupee amount.");
      return false;
    }
    if (allocatedAmounts.some((amount) => amount <= 0)) {
      toast.error("Each percentage must allocate at least one rupee.");
      return false;
    }
    if (
      allocatedAmounts.reduce((sum, amount) => sum + amount, 0) !==
      Number(capexInvoiceModule.invoice_module.total_amount)
    ) {
      toast.error("Calculated installment amounts must equal the total amount.");
      return false;
    }
  }

  // Collect selected labels + amounts
  const { allSelectedLabels } = collectLineItems(
    capexInvoiceModule.line_items
  );

  // Frequency 1 (single invoice)
  if (capexInvoiceModule.invoice_module.invoice_frequency_id === 1) {
    if (
      !validateFrequencyOne(
        capexInvoiceModule.invoice_module,
        allSelectedLabels
      )
    )
      return false;
  }

  return true;
};